import {
  ApiError,
  type CreatePaymentLinkInput,
  type PaymentLinkChannel,
  type PaymentLinkResult,
  createPaymentLink,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import { Check, Copy, Mail, MessageCircle, MessageSquare, X } from 'lucide-react';
import { useEffect, useState } from 'react';

export interface PaymentLinkTarget {
  purpose: 'booking_balance' | 'booking_deposit' | 'pos' | 'custom';
  bookingId?: string;
  posTransactionId?: string;
  customerId?: string;
}

interface SendPaymentLinkDrawerProps {
  target: PaymentLinkTarget;
  /** Pre-filled amount (outstanding balance / POS total). Null = let the server compute. */
  defaultAmountMinor?: number | null;
  /** Booking + custom links accept a staff-typed amount; POS links always charge the outstanding total. */
  allowAmountEdit?: boolean;
  currency: string;
  customerFirstName: string;
  /** When unknown, leave true — the server drops undeliverable channels and reports it. */
  hasEmail?: boolean;
  hasPhone?: boolean;
  open: boolean;
  onClose: () => void;
  onCreated?: (result: PaymentLinkResult) => void;
  /** `inline` renders the body without the fixed overlay (used inside the POS tender modal). */
  variant?: 'modal' | 'inline';
}

const CHANNEL_LABEL: Record<PaymentLinkChannel, string> = {
  email: 'email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

/**
 * "Send payment link" — mints a 7-day text-to-pay link (POST /api/payment-links)
 * and offers copy / email / SMS / WhatsApp. Mirrors SendWaiverLinkDrawer.
 *
 * Copy-link mints WITHOUT sending; the channel buttons mint a fresh link and
 * ask the server to deliver it through the comms pipeline. Re-minting for the
 * same booking / POS bill supersedes the earlier open link server-side so a
 * customer can never pay the same bill twice.
 */
export function SendPaymentLinkDrawer({
  target,
  defaultAmountMinor = null,
  allowAmountEdit = target.purpose !== 'pos',
  currency,
  customerFirstName,
  hasEmail = true,
  hasPhone = true,
  open,
  onClose,
  onCreated,
  variant = 'modal',
}: SendPaymentLinkDrawerProps) {
  const [amountInput, setAmountInput] = useState('');
  const [link, setLink] = useState<PaymentLinkResult | null>(null);
  const [busy, setBusy] = useState<'copy' | PaymentLinkChannel | null>(null);
  const [copied, setCopied] = useState(false);
  const [sentVia, setSentVia] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setLink(null);
      setBusy(null);
      setCopied(false);
      setSentVia(null);
      setError(null);
      return;
    }
    setAmountInput(defaultAmountMinor != null ? (defaultAmountMinor / 100).toFixed(2) : '');
  }, [open, defaultAmountMinor]);

  useEffect(() => {
    if (!open || variant !== 'modal') return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, variant]);

  if (!open) return null;

  function describeError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 403) return 'You do not have permission to send payment links.';
      if (err.code === 'connect_off') {
        return 'Online payments are not set up yet. Connect Stripe at Settings › Payments first.';
      }
      if (err.code === 'no_outstanding_balance') return 'Nothing is owed on this booking.';
      if (err.code === 'pos_transaction_requires_customer') {
        return 'Attach a customer to this sale before sending a payment link.';
      }
      if (err.code === 'payment_links_not_configured') {
        return 'Payment links are not configured on this deployment.';
      }
      return `Could not create the link (${err.status}).`;
    }
    return (err as Error).message ?? 'Could not create the link.';
  }

  function amountMinorFromInput(): number | undefined | null {
    if (!allowAmountEdit) return undefined;
    const trimmed = amountInput.trim();
    if (trimmed === '') return undefined;
    const minor = Math.round(Number.parseFloat(trimmed) * 100);
    if (!Number.isFinite(minor) || minor <= 0) return null;
    return minor;
  }

  async function mint(send: PaymentLinkChannel[]): Promise<PaymentLinkResult | null> {
    setError(null);
    const amountMinor = amountMinorFromInput();
    if (amountMinor === null) {
      setError('Enter a valid amount.');
      return null;
    }
    const body: CreatePaymentLinkInput = {
      purpose: target.purpose,
      ...(target.bookingId ? { bookingId: target.bookingId } : {}),
      ...(target.posTransactionId ? { posTransactionId: target.posTransactionId } : {}),
      ...(target.customerId ? { customerId: target.customerId } : {}),
      ...(amountMinor !== undefined ? { amountMinor } : {}),
      ...(send.length > 0 ? { send } : {}),
    };
    try {
      const res = await createPaymentLink(body);
      setLink(res);
      onCreated?.(res);
      return res;
    } catch (err) {
      setError(describeError(err));
      return null;
    }
  }

  async function handleCopy() {
    setBusy('copy');
    setCopied(false);
    try {
      const res = link ?? (await mint([]));
      if (!res) return;
      try {
        await navigator.clipboard.writeText(res.url);
        setCopied(true);
      } catch {
        setError('Could not copy automatically — select the link below to copy it.');
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleSend(channel: PaymentLinkChannel) {
    setBusy(channel);
    setSentVia(null);
    setCopied(false);
    try {
      const res = await mint([channel]);
      if (!res) return;
      if (res.sent) setSentVia(res.channels);
      else setError(`Could not send by ${CHANNEL_LABEL[channel]} — no contact details on file.`);
    } finally {
      setBusy(null);
    }
  }

  const body = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Text-to-pay
          </p>
          <h2 className="font-heading text-lg font-medium">Send payment link</h2>
        </div>
        {variant === 'modal' && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground hover:bg-secondary press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        )}
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground">
        {customerFirstName} can open the link on their phone and pay by card, Apple Pay or Google
        Pay. No portal sign-in is needed. The link lasts 7 days; sending a new one replaces any
        earlier link for the same bill.
      </p>

      {allowAmountEdit ? (
        <div className="space-y-1">
          <label
            htmlFor="payment-link-amount"
            className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Amount ({currency})
          </label>
          <input
            id="payment-link-amount"
            type="number"
            min="0.01"
            step="0.01"
            inputMode="decimal"
            value={amountInput}
            onChange={(e) => {
              setAmountInput(e.target.value);
              setLink(null);
              setCopied(false);
            }}
            disabled={busy !== null}
            className="num w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
          />
        </div>
      ) : (
        defaultAmountMinor != null && (
          <p className="text-sm">
            Amount:{' '}
            <span className="num font-medium">
              {formatPriceMinor(defaultAmountMinor, currency)}
            </span>
          </p>
        )
      )}

      <div className="grid gap-2">
        <button
          type="button"
          onClick={() => void handleCopy()}
          disabled={busy !== null}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50 press"
        >
          {copied ? (
            <Check className="size-4 text-success" strokeWidth={1.75} />
          ) : (
            <Copy className="size-4" strokeWidth={1.75} />
          )}
          {busy === 'copy' ? 'Creating link…' : copied ? 'Link copied' : 'Copy link'}
        </button>
        <button
          type="button"
          onClick={() => void handleSend('sms')}
          disabled={busy !== null || !hasPhone}
          title={hasPhone ? undefined : 'No mobile number on file'}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50 press"
        >
          <MessageSquare className="size-4" strokeWidth={1.75} />
          {busy === 'sms' ? 'Sending…' : 'Send by SMS'}
        </button>
        <button
          type="button"
          onClick={() => void handleSend('whatsapp')}
          disabled={busy !== null || !hasPhone}
          title={hasPhone ? undefined : 'No mobile number on file'}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50 press"
        >
          <MessageCircle className="size-4" strokeWidth={1.75} />
          {busy === 'whatsapp' ? 'Sending…' : 'Send by WhatsApp'}
        </button>
        <button
          type="button"
          onClick={() => void handleSend('email')}
          disabled={busy !== null || !hasEmail}
          title={hasEmail ? undefined : 'No email address on file'}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50 press"
        >
          <Mail className="size-4" strokeWidth={1.75} />
          {busy === 'email' ? 'Sending…' : 'Send by email'}
        </button>
      </div>

      {link && (
        <div className="space-y-1">
          <label
            htmlFor="payment-link-url"
            className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Link for {formatPriceMinor(link.link.amountMinor, link.link.currency)} (expires{' '}
            {new Date(link.expiresAt).toLocaleDateString('en-GB')})
          </label>
          <input
            id="payment-link-url"
            readOnly
            value={link.url}
            onFocus={(e) => e.currentTarget.select()}
            className="num w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs"
          />
        </div>
      )}

      {sentVia && sentVia.length > 0 && (
        <p role="status" className="flex items-center gap-1.5 text-sm text-success">
          <Check className="size-4" strokeWidth={1.75} />
          Sent by {sentVia.map((c) => CHANNEL_LABEL[c as PaymentLinkChannel] ?? c).join(' and ')}.
        </p>
      )}

      {error && (
        <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );

  if (variant === 'inline') {
    return <div data-testid="send-payment-link-inline">{body}</div>;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Send payment link"
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
        {body}
      </div>
    </div>
  );
}
